'use client'

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ChevronDown } from 'lucide-react'
import {
  phoneCountries,
  parsePhoneValue,
  formatPhoneValue,
  searchPhoneCountries,
  getPhoneCountryByCode,
  validatePhoneNumberLength,
  DEFAULT_PHONE_COUNTRY,
  type PhoneCountry,
} from '@/lib/phone-countries'

export interface PhoneInputProps {
  /** Combined phone value like "+966501234567" */
  value?: string
  /** Called with the combined phone value */
  onChange?: (value: string) => void
  onBlur?: () => void
  /** Default country ISO code (default: SA) */
  defaultCountry?: string
  disabled?: boolean
  placeholder?: string
  className?: string
  /** Show error state */
  error?: boolean
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      value = '',
      onChange,
      onBlur,
      defaultCountry = DEFAULT_PHONE_COUNTRY,
      disabled = false,
      placeholder = '5XX XXX XXXX',
      className,
      error = false,
    },
    ref
  ) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [touched, setTouched] = useState(false)
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Parse current value into parts
    const { countryCode: parsedCountry, localNumber: parsedLocal } = useMemo(
      () => parsePhoneValue(value),
      [value]
    )

    // Use parsed country or default
    const [selectedCountryCode, setSelectedCountryCode] = useState(
      parsedCountry || defaultCountry
    )
    const [localNumber, setLocalNumber] = useState(parsedLocal)

    // Sync internal state when value prop changes externally.
    // Only override the selected country when the incoming value actually
    // carries one — otherwise a transient empty value (e.g. a country was
    // picked but no digits typed yet) would wrongly snap back to the default.
    useEffect(() => {
      const { countryCode, localNumber: local } = parsePhoneValue(value)
      if (countryCode) setSelectedCountryCode(countryCode)
      setLocalNumber(local)
    }, [value])

    // R5-02: a defaultCountry that arrives AFTER mount (e.g. the hotel's
    // country read asynchronously) is adopted while nothing has decided the
    // country yet - the value carries none and the user has neither picked a
    // country nor typed. Once either happens the field keeps its country.
    const userDecidedRef = useRef(false)
    useEffect(() => {
      if (userDecidedRef.current || parsePhoneValue(value).countryCode) return
      setSelectedCountryCode(defaultCountry)
    }, [defaultCountry, value])

    const selectedCountry = useMemo(
      () => getPhoneCountryByCode(selectedCountryCode) ?? phoneCountries[0]!,
      [selectedCountryCode]
    )

    // Live validation — only show after user has interacted
    const lengthError = useMemo(() => {
      if (!touched || !localNumber.trim()) return null
      return validatePhoneNumberLength(localNumber, selectedCountryCode)
    }, [localNumber, selectedCountryCode, touched])

    const hasError = error || !!lengthError

    // Search results
    const filteredCountries = useMemo(
      () => (search ? searchPhoneCountries(search, 30) : phoneCountries),
      [search]
    )

    const emitChange = useCallback(
      (country: string, local: string) => {
        const combined = formatPhoneValue(country, local)
        onChange?.(combined)
      },
      [onChange]
    )

    const handleCountrySelect = useCallback(
      (country: PhoneCountry) => {
        userDecidedRef.current = true
        setSelectedCountryCode(country.code)
        setOpen(false)
        setSearch('')
        emitChange(country.code, localNumber)
      },
      [localNumber, emitChange]
    )

    const handleLocalChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setTouched(true)
        userDecidedRef.current = true

        // If the user types/pastes a leading "+", treat the field as a full
        // international number: auto-detect the country from the dial code and
        // keep only the national part in the field.
        if (val.trim().startsWith('+')) {
          const { countryCode, localNumber: local } = parsePhoneValue(val)
          const dial = getPhoneCountryByCode(countryCode)?.dialCode
          const normalized = val.replace(/[\s-]/g, '')
          if (dial && normalized.startsWith(dial)) {
            setSelectedCountryCode(countryCode)
            setLocalNumber(local)
            emitChange(countryCode, local)
            return
          }
          // Dial code not recognised yet (e.g. mid-typing "+4") — keep raw.
          setLocalNumber(val)
          onChange?.(val)
          return
        }

        setLocalNumber(val)
        emitChange(selectedCountryCode, val)
      },
      [selectedCountryCode, emitChange, onChange]
    )

    const handleBlur = useCallback(() => {
      setTouched(true)
      onBlur?.()
    }, [onBlur])

    // Auto-focus search when popover opens
    useEffect(() => {
      if (open) {
        setTimeout(() => searchInputRef.current?.focus(), 0)
      }
    }, [open])

    return (
      <div className="space-y-1">
        <div
          dir="ltr"
          style={{ fontFamily: 'var(--font-sans)' }}
          className={cn(
            'flex h-9 w-full rounded-md border bg-transparent shadow-xs transition-[color,box-shadow]',
            'dark:bg-input/30 border-input',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            hasError && 'border-destructive ring-destructive/20',
            disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
        >
          {/* Country code trigger */}
          <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className={cn(
                  'flex items-center gap-1 px-2 text-sm shrink-0 border-r border-input',
                  'hover:bg-muted/50 transition-colors rounded-l-md',
                  'focus-visible:outline-none',
                  disabled && 'pointer-events-none'
                )}
                aria-label="Select country code"
              >
                <span className="text-base leading-none">{selectedCountry.flag}</span>
                <span className="text-muted-foreground text-xs">{selectedCountry.dialCode}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[280px] p-0"
              align="start"
              side="bottom"
              sideOffset={4}
              dir="ltr"
            >
              <div className="flex flex-col" style={{ fontFamily: 'var(--font-sans)' }}>
                {/* Search */}
                <div className="border-b p-2">
                  <Input
                    ref={searchInputRef}
                    placeholder="Search country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8"
                  />
                </div>
                {/* Country list */}
                <div className="max-h-[240px] overflow-y-auto overscroll-contain">
                  {filteredCountries.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No countries found.
                    </div>
                  ) : (
                    filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => handleCountrySelect(country)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors',
                          selectedCountryCode === country.code && 'bg-muted'
                        )}
                      >
                        <span className="text-base leading-none shrink-0">{country.flag}</span>
                        <span className="truncate flex-1 text-left">{country.name}</span>
                        <span className="text-muted-foreground text-xs shrink-0">
                          {country.dialCode}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Local number input */}
          <input
            ref={ref}
            type="tel"
            inputMode="tel"
            value={localNumber}
            onChange={handleLocalChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'flex-1 bg-transparent px-3 py-1 text-base md:text-sm outline-none',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed'
            )}
          />
        </div>

        {/* Validation hint */}
        {lengthError && (
          <p className="text-xs text-destructive" dir="ltr" style={{ fontFamily: 'var(--font-sans)' }}>
            {lengthError === 'TOO_SHORT'
              ? 'Phone number is too short'
              : lengthError === 'TOO_LONG'
                ? 'Phone number is too long'
                : 'Invalid phone number length'}
          </p>
        )}
      </div>
    )
  }
)
